import React, { useMemo } from 'react';

import {
  START_DATE,
  USAGE_LIMIT,
  APPLICATION,
  CAMPAIGNS,
  STATUS,
  ID,
  NAME,
  PROMO_CODE,
} from 'scenes/PromoCodes/constants';
import { useLoadPromoCode } from 'scenes/PromoCodes/data';
import {
  useCreatePromoCode,
  useUpdatePromoCode,
  useCreatePromoCodeSet,
  useUpdatePromoCodeSet,
} from 'scenes/PromoCodes/data/mutations';

import PromoCodeForm from './PromoCodeForm';
import { promoCodeSchema, promoCodeSetSchema } from './schema';

export const PromoCodeSetFormCreate = ({
  initialValues: initialValuesProps = {},
  onSubmit,
  onClose,
  onExited,
  open,
  ...props
}) => {
  const [createPromoCodeSet, { isLoading }] = useCreatePromoCodeSet();

  const handleSubmit = async (values) => {
    await createPromoCodeSet(values);
    onSubmit();
  };

  const initialValues = useMemo(() => {
    return {
      [START_DATE]: new Date(),
      [USAGE_LIMIT]: 0,
      [APPLICATION]: 27,
      isSet: true,
      ...initialValuesProps,
    };
  }, []);

  return (
    <PromoCodeForm
      title={'Add Set of Personal Promo Codes'}
      validationSchema={promoCodeSetSchema}
      initialValues={initialValues}
      loading={isLoading}
      onClose={onClose}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      isSetFieldVisible={true}
      {...props}
    />
  );
};

export const PromoCodeSetFormEdit = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
  ...props
}) => {
  const { data, isLoading: loading } = useLoadPromoCode(id);
  const [updatePromoCodeSet, { isLoading }] = useUpdatePromoCodeSet();

  const handleSubmit = async (values) => {
    await updatePromoCodeSet(values);
    onSubmit();
  };

  if (data) {
    if (!data[APPLICATION]) {
      data[APPLICATION] = 27;
    }
  }

  return (
    <PromoCodeForm
      title={'Edit Set of Personal Promo Codes'}
      subTitle={data?.[NAME]}
      entityId={id}
      validationSchema={promoCodeSetSchema}
      active={!!data}
      initialValues={data}
      loading={loading || isLoading}
      onClose={onClose}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      isSetFieldVisible={true}
      {...props}
    />
  );
};

export const PublicPromoCodeFormCreate = ({
  initialValues: initialValuesProps = {},
  onSubmit,
  onClose,
  onExited,
  open,
  ...props
}) => {
  const [createPromoCode, { isLoading }] = useCreatePromoCode();

  const handleSubmit = async (values) => {
    await createPromoCode(values);
    onSubmit();
  };

  const initialValues = useMemo(() => {
    return {
      [START_DATE]: new Date(),
      [USAGE_LIMIT]: 0,
      [APPLICATION]: 27,
      isSet: false,
      ...initialValuesProps,
    };
  }, []);

  return (
    <PromoCodeForm
      title={'Add Public Promo Code'}
      validationSchema={promoCodeSchema}
      initialValues={initialValues}
      loading={isLoading}
      onClose={onClose}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      isPublicFieldVisible={true}
      {...props}
    />
  );
};

export const PublicPromoCodeFormEdit = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
  ...props
}) => {
  const { data, isLoading: loading } = useLoadPromoCode(id);
  const [updatePromoCode, { isLoading }] = useUpdatePromoCode();

  const handleSubmit = async (values) => {
    await updatePromoCode(values);
    onSubmit();
  };

  if (data) {
    if (!data[APPLICATION]) {
      data[APPLICATION] = 27;
    }
  }

  return (
    <PromoCodeForm
      title={'Edit Public Promo Code'}
      subTitle={data?.[NAME]}
      entityId={id}
      validationSchema={promoCodeSchema}
      active={!!data}
      initialValues={data}
      loading={loading || isLoading}
      onClose={onClose}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      isPublicFieldVisible={true}
      {...props}
    />
  );
};

export const PublicPromoCodeFormDuplicate = ({
  id,
  onSubmit,
  onClose,
  onExited,
  open,
  ...props
}) => {
  const { data, isLoading: loading } = useLoadPromoCode(id);
  const [createPromoCode, { isLoading }] = useCreatePromoCode();

  const handleSubmit = async (values) => {
    await createPromoCode(values);
    onSubmit();
  };

  if (data) {
    delete data[STATUS];
    delete data[ID];
    delete data[NAME];
    delete data[PROMO_CODE];
    delete data[CAMPAIGNS];
    if (!data[APPLICATION]) {
      data[APPLICATION] = 27;
    }
  }

  return (
    <PromoCodeForm
      title={'Duplicate Public Promo Code'}
      validationSchema={promoCodeSchema}
      active={!!data}
      initialValues={data}
      onClose={onClose}
      onSubmit={handleSubmit}
      onExited={onExited}
      open={open}
      loading={loading || isLoading}
      isPublicFieldVisible={true}
      {...props}
    />
  );
};
